import React, { useContext, useState, useEffect } from "react";
import UserContext from "../auth/UserContext";
import ValetApi from "../api/Api";
import { parseTimestamp } from "../utility/parseTimestamp";
import { getDateInputValueInTimeZone } from "../utility/dateInput";
import "./SurveyGraph.css";

function csvSafe(value) {
    if (value === null || value === undefined) return "";
    const str = String(value).replace(/"/g, '""');
    return `"${str}"`;
}

function getBarColor(value) {
    const numericValue = Number(value);
    if (numericValue >= 4) return "#16a34a";
    if (numericValue >= 3) return "#facc15";
    return "#dc2626";
}

function buildCsvString({ surveys, summary, locationName, podiumName, startDate, endDate }) {
    const metaRows = [["Report Type", "Survey Results"], ["Location", locationName || ""], ["Podium", podiumName || ""], ["Start Date", startDate || ""], ["End Date", endDate || ""], ["Generated At", new Date().toISOString()], ["Total Surveys", summary?.totalSurveys ?? 0], ["Q1 Avg", summary?.q1Avg ?? 0], ["Q2 Avg", summary?.q2Avg ?? 0], ["Q3 Avg", summary?.q3Avg ?? 0], ["Q4 Avg", summary?.q4Avg ?? 0], ["Q5 Avg", summary?.q5Avg ?? 0], ["Overall Avg", summary?.overallAvg ?? 0], []];

    const header = ["Submitted At", "Podium", "Ticket Number", "Parked By", "Retrieved By", "Q1", "Q2", "Q3", "Q4", "Q5", "Comments", "Transaction ID", "Survey ID"];

    const dataRows = surveys.map((s) => [parseTimestamp(s.submittedAt), s.podiumName || "", s.vehicleTicketNum, s.parkedByUserName || "", s.retrievedByUserName || "", s.q1Response, s.q2Response, s.q3Response, s.q4Response, s.q5Response, s.q6Response || "", s.transactionId, s.surveyId]);

    const allRows = [...metaRows, header, ...dataRows];
    return allRows.map((row) => row.map(csvSafe).join(",")).join("\n");
}

const EMPTY_ARR = Object.freeze([]);

const SurveyGraph = () => {
    const { userDetail } = useContext(UserContext);
    const accessibleLocations = userDetail?.accessibleLocations ?? EMPTY_ARR;
    const accessiblePodiums = userDetail?.accessiblePodiums ?? EMPTY_ARR;
    const userTimeZone = userDetail?.userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    const todayInUserTimeZone = getDateInputValueInTimeZone(new Date(), userTimeZone);

    const [selectedLocationId, setSelectedLocationId] = useState("");
    const [selectedPodiumId, setSelectedPodiumId] = useState("");
    const [startDate, setStartDate] = useState(todayInUserTimeZone);
    const [endDate, setEndDate] = useState(todayInUserTimeZone);
    const [summary, setSummary] = useState(null);
    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [didAutoLoad, setDidAutoLoad] = useState(false);

    const podiumOptions = accessiblePodiums.filter((p) => Number(p.locationId) === Number(selectedLocationId));

    useEffect(() => {
        if (!selectedLocationId && accessibleLocations.length > 0) {
            setSelectedLocationId(String(accessibleLocations[0].id));
        }
    }, [selectedLocationId, accessibleLocations]);

    useEffect(() => {
        if (!selectedLocationId) return;

        if (!podiumOptions.length) {
            setSelectedPodiumId("");
            return;
        }

        const exists = podiumOptions.some((p) => String(p.id) === String(selectedPodiumId));
        if (!exists) {
            setSelectedPodiumId(String(podiumOptions[0].id));
        }
    }, [selectedLocationId, selectedPodiumId, podiumOptions]);

    async function handleFetch(e) {
        if (e?.preventDefault) e.preventDefault();
        if (!selectedPodiumId) return;

        setLoading(true);
        setError("");
        try {
            const res = await ValetApi.getSurveyReportByPodiumDateRange(selectedPodiumId, startDate, endDate, userTimeZone);
            setSummary(res.data?.summary || null);
            setSurveys(res.data?.surveys || []);
        } catch (err) {
            setError(err?.[0] || "Could not fetch survey data.");
            setSummary(null);
            setSurveys([]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!didAutoLoad && selectedLocationId && selectedPodiumId) {
            handleFetch();
            setDidAutoLoad(true);
        }
    }, [didAutoLoad, selectedLocationId, selectedPodiumId]);

    function handleExportCsv() {
        if (!surveys.length) return;

        const csv = buildCsvString({
            surveys,
            summary,
            podiumName: podiumOptions.find((p) => String(p.id) === String(selectedPodiumId))?.name || "",
            locationName: accessibleLocations.find((l) => String(l.id) === String(selectedLocationId))?.name || "",
            startDate,
            endDate,
        });

        const fileName = `survey_report_${selectedLocationId}_${selectedPodiumId}_${startDate}_to_${endDate}.csv`;
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    const bars = summary
        ? [
              { label: "Q1", value: summary.q1Avg },
              { label: "Q2", value: summary.q2Avg },
              { label: "Q3", value: summary.q3Avg },
              { label: "Q4", value: summary.q4Avg },
              { label: "Q5", value: summary.q5Avg },
          ]
        : [];

    return (
        <div className="survey-page page-scroll">
            <div className="survey-card page-scroll-inner">
                <div className="survey-header">
                    <h2>Survey Results</h2>
                    <p>Filter by location, podium, and date range.</p>
                </div>

                {!accessibleLocations.length ? (
                    <div className="survey-empty">No accessible locations found.</div>
                ) : (
                    <>
                        <form
                            className="survey-graph-form"
                            onSubmit={handleFetch}>
                            <label>
                                Location
                                <select
                                    value={selectedLocationId}
                                    onChange={(e) => {
                                        setSelectedLocationId(e.target.value);
                                        setDidAutoLoad(false);
                                    }}>
                                    {accessibleLocations.map((l) => (
                                        <option
                                            key={l.id}
                                            value={l.id}>
                                            {l.name}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label>
                                Podium
                                <select
                                    value={selectedPodiumId}
                                    onChange={(e) => setSelectedPodiumId(e.target.value)}
                                    disabled={!podiumOptions.length}>
                                    {podiumOptions.map((p) => (
                                        <option
                                            key={p.id}
                                            value={p.id}>
                                            {p.name}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label>
                                Start Date
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                />
                            </label>

                            <label>
                                End Date
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                />
                            </label>

                            <button
                                type="submit"
                                disabled={loading || !selectedPodiumId}>
                                {loading ? "Loading..." : "Fetch"}
                            </button>

                            <button
                                type="button"
                                className="survey-export-btn"
                                onClick={handleExportCsv}
                                disabled={loading || surveys.length === 0}>
                                Export CSV
                            </button>
                        </form>

                        {error && <div className="survey-error">{error}</div>}

                        {summary && (
                            <div className="survey-summary">
                                <div>Total Surveys: {summary.totalSurveys}</div>
                                <div>Overall Avg (Q1-Q5): {summary.overallAvg}</div>
                            </div>
                        )}

                        {bars.length > 0 && (
                            <div className="survey-bars">
                                {bars.map((b) => (
                                    <div
                                        className="survey-bar-row"
                                        key={b.label}>
                                        <span className="survey-bar-label">{b.label}</span>
                                        <div className="survey-bar-track">
                                            <div
                                                className="survey-bar-fill"
                                                style={{ width: `${(Number(b.value) / 5) * 100}%`, background: getBarColor(b.value) }}
                                            />
                                        </div>
                                        <span className="survey-bar-value">{b.value}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="survey-table-wrap">
                            <table className="survey-table">
                                <thead>
                                    <tr>
                                        <th>Submitted</th>
                                        <th>Podium</th>
                                        <th>Ticket</th>
                                        <th>Parked By</th>
                                        <th>Retrieved By</th>
                                        <th>Q1</th>
                                        <th>Q2</th>
                                        <th>Q3</th>
                                        <th>Q4</th>
                                        <th>Q5</th>
                                        <th>Comments</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {surveys.map((s) => (
                                        <tr key={s.surveyId}>
                                            <td>{parseTimestamp(s.submittedAt)}</td>
                                            <td>{s.podiumName || ""}</td>
                                            <td>{s.vehicleTicketNum}</td>
                                            <td>{s.parkedByUserName || ""}</td>
                                            <td>{s.retrievedByUserName || ""}</td>
                                            <td>{s.q1Response}</td>
                                            <td>{s.q2Response}</td>
                                            <td>{s.q3Response}</td>
                                            <td>{s.q4Response}</td>
                                            <td>{s.q5Response}</td>
                                            <td>{s.q6Response || ""}</td>
                                        </tr>
                                    ))}
                                    {!loading && surveys.length === 0 && (
                                        <tr>
                                            <td colSpan="11">No survey data found.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SurveyGraph;
